<script setup>
/**
 * Grade Alignment (PD-1293) — the read-across between the client's own grade chart
 * and the RoleMapper ladder. Ported from the design prototype's LvlFrameworkAlignTab
 * + LvlAlignGrid (lvl/mapping.jsx).
 *
 * ONE BACKBONE. The RoleMapper ladder runs down the left and never moves; the
 * client's grades are placed against it, never the other way round. Each grade sits
 * at the level its EVALUATED roles actually score at, so the two can be read across
 * in either language — "our Grade 11 is their Level 6".
 *
 * The default view is deliberately the simple story: one ladder, one grade chart,
 * whole company. A column per function group appears only where the evidence
 * actually disagrees, and a second (inherited) grade chart is opt-in — otherwise the
 * page stops being one framework and becomes a spreadsheet.
 *
 * Read-only by design: governing happens in the mapping panel, which is the only
 * surface that changes a link or publishes a version.
 */
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import axios from 'axios';
import { ANCHOR_FLOOR } from '@rm-ui/levelling/anchorSetup';
import {
  gradeSuggestions, resolveMap, isOverridden, gradeSplits, divergentFamilyGroups, compareGrades,
} from '@rm-ui/levelling/gradeMapping';
import GradeMappingPanel from '@/components/levelling/GradeMappingPanel.vue';

const props = defineProps({
  /** The framework as framework.vue holds it: levels, functions, tracks, level_function_tracks + labels. */
  levellingFramework: { type: Object, required: true },
  /* See FrameworkInUseView: mounted once, shown and hidden, so it reloads when it
     comes back into view rather than on every mount. */
  active: { type: Boolean, default: true },
});

const loading = ref(true);
const error = ref('');
const roles = ref([]);
const orgSize = ref('standard');
const schemes = ref([]);
const overrides = ref([]);
const versions = ref([]);
const published = ref(null);
const stale = ref(null);
const retiredOverrides = ref(0);
const canGovern = ref(false);
const enabled = ref(false);

const howOpen = ref(false);
const showFg = ref([]);
const showOthers = ref([]);
const drill = ref(null);
const panelOpen = ref(false);

/* ------------------------------------------------------------------ the ladder */

const lf = computed(() => props.levellingFramework || {});
const levelLabelWord = computed(() => lf.value.level_label || 'Level');

/** Most junior first, matching the Levels and Tracks default reading order. */
const levels = computed(() => [...(lf.value.levels || [])].sort((a, b) => a.position - b.position));
const levelById = computed(() => {
  const m = {};
  for (const l of levels.value) m[l.lf_level_id] = l;
  return m;
});

/* The backbone label is read from the core function's rungs, the same source Levels
   and Tracks shows: the real cell names and abbreviations for that level. Cell names
   are composites ("Individual Contributor - IC10 - Thought Leader") and the code is
   already on the line below, so only the last segment is kept. */
const coreFunctionId = computed(() => {
  const list = lf.value.functions || [];
  const core = list.find(f => f.is_core);
  return (core || list[0] || {}).lf_function_id ?? null;
});
const levelMeta = computed(() => {
  const out = {};
  for (const lft of lf.value.level_function_tracks || []) {
    if (lft.lf_function_id !== coreFunctionId.value) continue;
    const e = out[lft.lf_level_id] || (out[lft.lf_level_id] = { names: [], codes: [] });
    const full = lft.name || '';
    const nm = (full.split(/\s+[-–]\s+/).pop() || full).trim();
    if (nm && !e.names.includes(nm)) e.names.push(nm);
    if (lft.abbreviation) e.codes.push(lft.abbreviation);
  }
  return out;
});
const backboneNames = (id) => {
  const m = levelMeta.value[id];
  const names = m && m.names.length ? m.names.join(' · ') : null;
  return names || `${levelLabelWord.value} ${(levelById.value[id] || {}).name ?? ''}`.trim();
};
const backboneCodes = (id) => {
  const m = levelMeta.value[id];
  return m && m.codes.length ? m.codes.join(' · ') : '';
};
const levelName = id => (levelById.value[id] || {}).name ?? '';
const levelPosition = id => (levelById.value[id] || {}).position ?? null;

/* -------------------------------------------------------------- the read-across */

const floor = computed(() => ANCHOR_FLOOR[orgSize.value] ?? ANCHOR_FLOOR.standard);
const suggestions = computed(() => gradeSuggestions(roles.value, { floor: floor.value }));
const map = computed(() => resolveMap(suggestions.value, overrides.value));

const schemeById = computed(() => {
  const m = {};
  for (const s of schemes.value) m[s.tpa_id] = s;
  return m;
});

/* The primary chart is the one most of the register is actually on — not whichever
   template happens to sort first. Everything else is "inherited", off by default. */
const primary = computed(() => {
  const counts = {};
  for (const s of Object.values(suggestions.value)) counts[s.tpa_id] = (counts[s.tpa_id] || 0) + s.total;
  const ranked = schemes.value.map(s => s.tpa_id).sort((a, b) => (counts[b] || 0) - (counts[a] || 0));
  return ranked[0] ?? null;
});
const inherited = computed(() => schemes.value.map(s => s.tpa_id).filter(id => id !== primary.value));

const divGroups = computed(() => divergentFamilyGroups(suggestions.value, map.value).slice(0, 4));
const splits = computed(() => gradeSplits(suggestions.value, map.value, showFg.value));

/** Grades of one scheme that land on one level — optionally as seen by one family group. */
const cellGrades = (tpaId, levelId, familyGroup) => {
  const out = [];
  for (const s of Object.values(suggestions.value)) {
    if (s.tpa_id !== tpaId) continue;
    if (familyGroup) {
      const tally = s.familyGroups[familyGroup];
      if (!tally) continue;
      const total = Object.values(tally).reduce((a, b) => a + b, 0);
      if (total < 2) continue;
      const there = Number(Object.keys(tally).sort((x, y) => tally[y] - tally[x] || String(x).localeCompare(String(y)))[0]);
      if (there !== levelId) continue;
    } else if (map.value[s.key] !== levelId) continue;
    out.push(s);
  }
  return out.sort((a, b) => compareGrades(a.grade, b.grade));
};

/** How a grade reads in a function-group column: agreeing with the company link, or above/below it. */
const groupDirection = (s, familyGroup) => {
  const home = map.value[s.key];
  const tally = s.familyGroups[familyGroup];
  if (home == null || !tally) return null;
  const there = Number(Object.keys(tally).sort((x, y) => tally[y] - tally[x] || String(x).localeCompare(String(y)))[0]);
  if (there === home) return null;
  const a = levelPosition(there); const b = levelPosition(home);
  return a != null && b != null && a > b ? 'up' : 'down';
};

const pillClass = (s, familyGroup) => {
  if (familyGroup) return groupDirection(s, familyGroup) ? 'is-div' : 'is-agree';
  return s.state === 'thin' ? 'is-thin' : s.divergent ? 'is-div' : 'is-conf';
};

const pillTip = (s, familyGroup) => {
  let tip;
  if (familyGroup) {
    const dir = groupDirection(s, familyGroup);
    tip = dir
      ? `Sits one or more ${levelLabelWord.value.toLowerCase()}s ${dir === 'up' ? 'higher' : 'lower'} in this function than it does company-wide — that is the split, and it routes to review.`
      : 'Agrees with the company-wide link for this grade.';
  } else if (s.state === 'thin') {
    tip = `Low confidence: only ${s.total} evaluated role${s.total === 1 ? '' : 's'} behind this link, below the floor of ${s.floor}. Review before relying on it.`;
  } else if (s.divergent) {
    tip = `Divergence: this grade sits at different ${levelLabelWord.value.toLowerCase()}s depending on the function.`;
  } else {
    tip = `Confident: calibrated against enough evaluated roles (${Math.round(s.agreement * 100)}% agree).`;
  }
  if (isOverridden(s, map.value)) {
    tip += ` — OVERRIDDEN by hand: the evidence points at ${backboneNames(s.lf_level_id)}, this is set to `
      + `${map.value[s.key] == null ? 'unmapped' : backboneNames(map.value[s.key])}. The confidence describes the evidence, not the override.`;
  }
  return `${tip} Select to list the roles on this grade.`;
};

/* --------------------------------------------------------------------- drilling */

const openDrill = (s) => {
  drill.value = drill.value && drill.value.key === s.key ? null : s;
};
const drillRoles = computed(() => {
  const s = drill.value;
  if (!s) return [];
  const home = map.value[s.key];
  const homePos = home == null ? null : levelPosition(home);
  return roles.value
    .filter(r => r.gradeTpaId === s.tpa_id && String(r.legacyGrade || '').trim().toUpperCase() === String(s.grade).toUpperCase())
    .map(r => ({
      ...r,
      delta: homePos != null && r.levelPosition != null ? r.levelPosition - homePos : null,
    }))
    .sort((a, b) => Math.abs(b.delta || 0) - Math.abs(a.delta || 0)
      || String(a.family).localeCompare(String(b.family))
      || String(a.title).localeCompare(String(b.title)));
});
const drillOff = computed(() => drillRoles.value.filter(r => r.delta).length);
const drillLevelled = computed(() => drillRoles.value.filter(r => r.levelPosition != null).length);

/* Away from the link first, then on it, then not levelled. A long "on" list is
   folded only when there are divergent roles above it to keep in view. */
const drillAll = ref(false);
const drillGroups = computed(() => {
  const s = drill.value;
  if (!s) return [];
  const home = map.value[s.key];
  const word = levelLabelWord.value;
  const off = drillRoles.value.filter(r => r.delta);
  const on = drillRoles.value.filter(r => r.levelPosition != null && !r.delta);
  const none = drillRoles.value.filter(r => r.levelPosition == null);
  return [
    { key: 'off', label: home != null ? `Away from ${word} ${levelName(home)}` : `Away from this ${word.toLowerCase()}`, items: off },
    { key: 'on', label: home != null ? `On ${word} ${levelName(home)}` : 'Levelled', items: on, fold: off.length > 0 && on.length > 6 && !drillAll.value },
    { key: 'none', label: 'Not levelled', items: none },
  ].filter(g => g.items.length);
});

const onDrillKey = (e) => { if (e.key === 'Escape') drill.value = null; };
watch(drill, (s, was) => {
  drillAll.value = false;
  if (s && !was) window.addEventListener('keydown', onDrillKey);
  else if (!s && was) window.removeEventListener('keydown', onDrillKey);
});
onBeforeUnmount(() => window.removeEventListener('keydown', onDrillKey));

/* --------------------------------------------------------------------- loading */

const toggle = (list, v) => {
  const i = list.value.indexOf(v);
  if (i < 0) list.value = [...list.value, v]; else list.value = list.value.filter(x => x !== v);
};
/* The template unwraps refs, so `toggle(showFg, fg)` there would hand over the bare
   array rather than the ref — these bind the refs on the script side instead. */
const toggleFg = fg => toggle(showFg, fg);
const toggleOther = id => toggle(showOthers, id);

/* Hand-set links counted only across the CHARTS ON SCREEN. Counting every scheme
   showed "5 overridden" beside four visible marks, because an inherited chart is off
   by default — a count that does not match what is in front of you. */
const shownSchemes = computed(() => [primary.value, ...showOthers.value].filter(x => x != null));
const overriddenShown = computed(() => Object.values(suggestions.value)
  .filter(s => shownSchemes.value.includes(s.tpa_id) && isOverridden(s, map.value)).length);

const gridColumns = computed(() =>
  `220px repeat(${1 + showFg.value.length + showOthers.value.length}, minmax(150px, 1fr))`);

async function load() {
  loading.value = true;
  error.value = '';
  try {
    const [fwResp, gmResp] = await Promise.all([
      axios.post('document/frameworkInUse', {}),
      axios.post('document/gradeMapping', {}),
    ]);
    const fw = (fwResp && fwResp.data && fwResp.data.Data) || {};
    const gm = (gmResp && gmResp.data && gmResp.data.Data) || {};
    roles.value = Array.isArray(fw.roles) ? fw.roles : [];
    orgSize.value = fw.orgSize || 'standard';
    schemes.value = Array.isArray(gm.schemes) ? gm.schemes : [];
    overrides.value = Array.isArray(gm.overrides) ? gm.overrides : [];
    versions.value = Array.isArray(gm.versions) ? gm.versions : [];
    published.value = gm.published || null;
    stale.value = gm.stale || null;
    retiredOverrides.value = Number(gm.retiredOverrides) || 0;
    canGovern.value = !!gm.canGovern;
    enabled.value = !!gm.enabled;
    showFg.value = [];
    showOthers.value = [];
  } catch (e) {
    error.value = 'Could not load the grade read-across.';
    roles.value = [];
  } finally {
    loading.value = false;
  }
}

const onPanelSaved = () => load();

onMounted(load);
/* The panel is fixed to the viewport, so a hidden tab must not leave it open over its sibling. */
watch(() => props.active, (on, was) => { if (!on) drill.value = null; else if (was === false) load(); });
</script>

<template>
  <div class="lvl-fwal">
    <div v-if="loading" class="lvl-fwal-loading">Loading the read-across&hellip;</div>
    <div v-else-if="error" class="lvl-fwal-msg is-error">{{ error }}</div>
    <div v-else-if="!enabled" class="lvl-fwal-msg">
      Grade alignment is not switched on for this client.
    </div>
    <div v-else-if="!schemes.length" class="lvl-fwal-msg">
      No grade is captured on this register yet. Mark a template attribute as
      <strong>Legacy Grade</strong> in the template builder, and the grades on your roles will line
      up against the framework here.
    </div>

    <template v-else>
      <!-- ============================= intro ============================= -->
      <div class="lvl-fwal-intro" data-tour="fwal-intro">
        <span class="lvl-fwal-intro-ic"><v-icon size="17">mdi-format-horizontal-align-left</v-icon></span>
        <div class="lvl-fwal-intro-main">
          <span class="t">&ldquo;Our Grade 11 is their {{ levelLabelWord }} 6&rdquo;</span>
          <span class="s">
            Each of your grades sits at the {{ levelLabelWord.toLowerCase() }} its evaluated roles
            actually score at, so you can read across in either language.
            <button type="button" class="lvl-fwal-more" :aria-expanded="howOpen" @click="howOpen = !howOpen">
              {{ howOpen ? 'Hide detail' : 'How this works' }}
            </button>
          </span>
          <span v-if="howOpen" class="s lvl-fwal-how">
            The RoleMapper ladder on the left never changes &mdash; your grades are placed against it,
            never the other way round. Nothing here edits a grade or a
            {{ levelLabelWord.toLowerCase() }}: it is the published link classification uses as a
            starting point, and it is transitional by design, so it is meant to fall away as the
            business moves onto RoleMapper {{ levelLabelWord.toLowerCase() }}s.
          </span>
          <span v-if="retiredOverrides" class="s lvl-fwal-how">
            {{ retiredOverrides }} hand-set link{{ retiredOverrides === 1 ? ' was' : 's were' }} made
            against an earlier version of the ladder and no longer apply &mdash; those grades have gone
            back to their suggested {{ levelLabelWord.toLowerCase() }}.
          </span>
        </div>
        <span class="lvl-fwal-actions">
          <v-tooltip bottom content-class="fw-tip">
            <template v-slot:activator="{ on, attrs }">
              <span class="lvl-fwal-status" :class="{ 'is-pub': published }" v-bind="attrs" v-on="on">{{
                published ? `Published v${published.n} · transitional` : 'Draft · not published'
              }}{{ overriddenShown ? ` · ${overriddenShown} overridden` : '' }}</span>
            </template>
            <span>{{ published
              ? 'Published links are dated and versioned, and are meant to be temporary — a bridge while the business moves onto RoleMapper levels.'
              : 'Nothing is published yet, so classification is not pre-filled from grades.' }}</span>
          </v-tooltip>
          <v-tooltip bottom content-class="fw-tip">
            <template v-slot:activator="{ on, attrs }">
              <button type="button" class="lvl-fwal-btn" v-bind="attrs" v-on="on" @click="panelOpen = true">
                <v-icon size="16">mdi-swap-horizontal</v-icon> Grades &rarr; {{ levelLabelWord.toLowerCase() }}s
              </button>
            </template>
            <span>Open the mapping panel: review the suggested {{ levelLabelWord.toLowerCase() }} for each
              grade, override it, pause a chart, or publish a new dated version.</span>
          </v-tooltip>
        </span>
      </div>

      <div v-if="stale" class="lvl-fwal-msg is-warn">
        <v-icon size="16">mdi-alert-outline</v-icon>
        Version {{ stale.n }} was published against a different ladder, so it is no longer in force
        &mdash; classification is not being pre-filled from it. Review the links below and publish again.
      </div>

      <!-- ============================== chips ============================= -->
      <div v-if="divGroups.length || inherited.length" class="lvl-fwal-fgbar">
        <template v-if="divGroups.length">
          <span class="lvl-fwal-fglabel">
            <v-icon size="14">mdi-call-split</v-icon>
            <span>Evidence disagrees in {{ divGroups.length }} function{{ divGroups.length === 1 ? '' : 's' }}:</span>
          </span>
          <v-tooltip v-for="fg in divGroups" :key="fg" bottom content-class="fw-tip">
            <template v-slot:activator="{ on, attrs }">
              <button
                type="button"
                class="lvl-chipbtn"
                :class="{ 'is-on': showFg.includes(fg) }"
                :aria-pressed="showFg.includes(fg)"
                v-bind="attrs"
                v-on="on"
                @click="toggleFg(fg)"
              ><v-icon v-if="showFg.includes(fg)" size="14">mdi-check</v-icon> {{ fg }}</button>
            </template>
            <span>{{ showFg.includes(fg) ? `Hide the ${fg} column.`
              : `Add a column showing how ${fg} grades line up — only these functions differ from the company-wide link.` }}</span>
          </v-tooltip>
        </template>
        <template v-if="inherited.length">
          <span class="lvl-fwal-fglabel lvl-fwal-fglabel--sep">
            <v-icon size="14">mdi-file-tree-outline</v-icon>
            <span>Other grade chart{{ inherited.length === 1 ? '' : 's' }}:</span>
          </span>
          <v-tooltip v-for="id in inherited" :key="id" bottom content-class="fw-tip">
            <template v-slot:activator="{ on, attrs }">
              <button
                type="button"
                class="lvl-chipbtn"
                :class="{ 'is-on': showOthers.includes(id) }"
                :aria-pressed="showOthers.includes(id)"
                v-bind="attrs"
                v-on="on"
                @click="toggleOther(id)"
              >
                <v-icon size="14">{{ showOthers.includes(id) ? 'mdi-check' : 'mdi-plus' }}</v-icon>
                {{ (schemeById[id] || {}).label }}
              </button>
            </template>
            <span>A second grade chart on this register &mdash; typically inherited with an acquisition.
              Off by default; add it to line it up against the same backbone.</span>
          </v-tooltip>
        </template>
      </div>

      <!-- =============================== key ============================== -->
      <div class="lvl-mapal-key">
        <span class="lvl-mapal-key-i"><span class="sw is-bb"></span> The fixed RoleMapper backbone</span>
        <span class="lvl-mapal-key-i"><span class="lvl-grade-pill lvl-mapal-g is-conf">G</span> Confident &mdash; enough evidence</span>
        <span class="lvl-mapal-key-i"><span class="lvl-grade-pill lvl-mapal-g is-thin">G</span> Low confidence &mdash; needs review</span>
        <span class="lvl-mapal-key-i"><span class="lvl-grade-pill lvl-mapal-g is-div">G</span> Divergence &mdash; differs by function</span>
        <span class="lvl-mapal-key-i"><span class="lvl-grade-pill lvl-mapal-g is-conf is-ov">G</span> Overridden by hand</span>
        <span class="lvl-mapal-key-i"><span class="lvl-mapal-none">&ndash;</span> No grade at this {{ levelLabelWord.toLowerCase() }}</span>
      </div>

      <!-- ============================== grid ============================== -->
      <div class="lvl-mapal-grid" :style="{ gridTemplateColumns: gridColumns }" data-tour="fwal-grid">
        <span class="lvl-mapal-slot">
          <v-tooltip bottom content-class="fw-tip">
            <template v-slot:activator="{ on, attrs }">
              <span class="lvl-mapal-h lvl-mapal-h--bb" v-bind="attrs" v-on="on">RoleMapper {{ levelLabelWord.toLowerCase() }}</span>
            </template>
            <span>The RoleMapper ladder never changes. Everything to the right is your own grade language lined up against it.</span>
          </v-tooltip>
        </span>
        <span class="lvl-mapal-slot">
          <v-tooltip bottom content-class="fw-tip">
            <template v-slot:activator="{ on, attrs }">
              <span class="lvl-mapal-h" v-bind="attrs" v-on="on">{{ (schemeById[primary] || {}).label }} &middot; whole company{{ (schemeById[primary] || {}).paused ? ' · paused' : '' }}</span>
            </template>
            <span>Your grades as they apply across the whole company &mdash; the default link. This is what
              pre-fills classification once published.</span>
          </v-tooltip>
        </span>
        <span v-for="fg in showFg" :key="'h' + fg" class="lvl-mapal-slot">
          <v-tooltip bottom content-class="fw-tip">
            <template v-slot:activator="{ on, attrs }">
              <span class="lvl-mapal-h lvl-mapal-h--fg" v-bind="attrs" v-on="on">{{ fg }}</span>
            </template>
            <span>How {{ fg }} grades line up. This column exists only because the evidence here disagrees
              with the company-wide link.</span>
          </v-tooltip>
        </span>
        <span v-for="id in showOthers" :key="'ho' + id" class="lvl-mapal-slot">
          <v-tooltip bottom content-class="fw-tip">
            <template v-slot:activator="{ on, attrs }">
              <span class="lvl-mapal-h lvl-mapal-h--other" v-bind="attrs" v-on="on">{{ (schemeById[id] || {}).label }}{{ (schemeById[id] || {}).paused ? ' · paused' : '' }}</span>
            </template>
            <span>A second grade chart on this register, lined up against the same backbone.</span>
          </v-tooltip>
        </span>

        <template v-for="l in levels">
          <span :key="'bb' + l.lf_level_id" class="lvl-mapal-slot">
            <v-tooltip bottom content-class="fw-tip">
              <template v-slot:activator="{ on, attrs }">
                <span class="lvl-mapal-bb" v-bind="attrs" v-on="on">
                  <b>{{ backboneNames(l.lf_level_id) }}</b>
                  <i>{{ levelLabelWord }} {{ l.name }}{{ backboneCodes(l.lf_level_id) ? ' · ' + backboneCodes(l.lf_level_id) : '' }}</i>
                </span>
              </template>
              <span>{{ backboneNames(l.lf_level_id) }} &middot; {{ levelLabelWord }} {{ l.name
                }}{{ backboneCodes(l.lf_level_id) ? ' · ' + backboneCodes(l.lf_level_id) : '' }}</span>
            </v-tooltip>
          </span>

          <span :key="'c' + l.lf_level_id" class="lvl-mapal-cell">
            <template v-if="cellGrades(primary, l.lf_level_id, null).length">
              <v-tooltip v-for="s in cellGrades(primary, l.lf_level_id, null)" :key="s.key" bottom content-class="fw-tip" open-delay="250">
                <template v-slot:activator="{ on, attrs }">
                  <button
                    type="button"
                    class="lvl-grade-pill lvl-mapal-g is-btn"
                    :class="[pillClass(s, null), { 'is-ov': isOverridden(s, map), 'is-open': drill && drill.key === s.key }]"
                    v-bind="attrs"
                    v-on="on"
                    @click="openDrill(s)"
                  >{{ s.grade }}</button>
                </template>
                <span>{{ pillTip(s, null) }}</span>
              </v-tooltip>
            </template>
            <v-tooltip v-else bottom content-class="fw-tip">
              <template v-slot:activator="{ on, attrs }">
                <span class="lvl-mapal-none" v-bind="attrs" v-on="on">&ndash;</span>
              </template>
              <span>No grade in this chart lines up with this RoleMapper {{ levelLabelWord.toLowerCase() }}.</span>
            </v-tooltip>
          </span>

          <span v-for="fg in showFg" :key="'f' + fg + l.lf_level_id" class="lvl-mapal-cell lvl-mapal-cell--fg">
            <template v-if="cellGrades(primary, l.lf_level_id, fg).length">
              <v-tooltip v-for="s in cellGrades(primary, l.lf_level_id, fg)" :key="fg + s.key" bottom content-class="fw-tip" open-delay="250">
                <template v-slot:activator="{ on, attrs }">
                  <button
                    type="button"
                    class="lvl-grade-pill lvl-mapal-g is-btn"
                    :class="[pillClass(s, fg), { 'is-open': drill && drill.key === s.key }]"
                    v-bind="attrs"
                    v-on="on"
                    @click="openDrill(s)"
                  >{{ s.grade }}<v-icon v-if="groupDirection(s, fg)" size="11">{{
                    groupDirection(s, fg) === 'up' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}</v-icon></button>
                </template>
                <span>{{ pillTip(s, fg) }}</span>
              </v-tooltip>
            </template>
            <span v-else class="lvl-mapal-none">&ndash;</span>
          </span>

          <span v-for="id in showOthers" :key="'o' + id + l.lf_level_id" class="lvl-mapal-cell lvl-mapal-cell--other">
            <template v-if="cellGrades(id, l.lf_level_id, null).length">
              <v-tooltip v-for="s in cellGrades(id, l.lf_level_id, null)" :key="s.key" bottom content-class="fw-tip" open-delay="250">
                <template v-slot:activator="{ on, attrs }">
                  <button
                    type="button"
                    class="lvl-grade-pill lvl-mapal-g is-btn"
                    :class="[pillClass(s, null), { 'is-ov': isOverridden(s, map), 'is-open': drill && drill.key === s.key }]"
                    v-bind="attrs"
                    v-on="on"
                    @click="openDrill(s)"
                  >{{ s.grade }}</button>
                </template>
                <span>{{ pillTip(s, null) }}</span>
              </v-tooltip>
            </template>
            <span v-else class="lvl-mapal-none">&ndash;</span>
          </span>
        </template>
      </div>

      <!-- ============================== drill ============================= -->
      <!-- design: a right-hand panel over a scrim (.lvl-drill), so the grid stays
           in place while a grade's roles are read rather than being pushed down. -->
      <div v-if="drill" class="lvl-drill" role="dialog" :aria-label="`Roles on grade ${drill.grade}`">
        <div class="lvl-drill-scrim" @click="drill = null"></div>
        <div class="lvl-drill-panel">
          <div class="lvl-drill-head">
            <div class="lvl-drill-head-top">
              <div class="lvl-mapal-drill-hd">
                <span class="lvl-grade-pill lvl-mapal-g" :class="pillClass(drill, null)">{{ drill.grade }}</span>
                <h3>{{ map[drill.key] != null
                  ? `Links to ${backboneNames(map[drill.key])} (${levelLabelWord} ${levelName(map[drill.key])}) company-wide`
                  : `Not linked to a ${levelLabelWord.toLowerCase()} yet` }}</h3>
              </div>
              <button type="button" class="lvl-mapal-drill-x" aria-label="Close" @click="drill = null">
                <v-icon size="18">mdi-close</v-icon>
              </button>
            </div>
            <div class="lvl-mapal-drill-meta">
              <span class="lvl-mapal-drill-n">
                {{ drillRoles.length }} role{{ drillRoles.length === 1 ? '' : 's' }} &middot;
                {{ drillLevelled }} levelled{{ drillOff ? ` · ${drillOff} away from this ${levelLabelWord.toLowerCase()}` : '' }}
              </span>
              <v-tooltip v-if="isOverridden(drill, map)" bottom content-class="fw-tip">
                <template v-slot:activator="{ on, attrs }">
                  <span class="lvl-map-state is-ov" v-bind="attrs" v-on="on">Overridden &middot; evidence says
                    {{ levelLabelWord }} {{ levelName(drill.lf_level_id) }}</span>
                </template>
                <span>Set by hand. The evidence behind this grade points at {{ backboneNames(drill.lf_level_id) }};
                  an admin moved the link. The confidence describes the evidence, which the override did not change.</span>
              </v-tooltip>
            </div>
          </div>
          <div class="lvl-drill-body">
            <template v-if="drillRoles.length">
              <!-- Divergent roles grouped first: they are why an amber grade is amber. -->
              <section v-for="g in drillGroups" :key="g.key" class="lvl-mapal-drill-grp">
                <div class="lvl-mapal-drill-gh">
                  <span class="lvl-mapal-drill-t">{{ g.label }}</span>
                  <span class="lvl-mapal-drill-n">{{ g.items.length }}</span>
                </div>
                <div class="lvl-mapal-drill">
                  <div
                    v-for="r in (g.fold ? g.items.slice(0, 5) : g.items)"
                    :key="r.doc_id"
                    class="lvl-mapal-drill-row"
                    :class="{ 'is-off': !!r.delta }"
                  >
                    <span class="lvl-mapal-drill-who">
                      <span class="t">{{ r.title }}</span>
                      <span class="f">{{ [r.reference, r.family].filter(Boolean).join(' · ') }}</span>
                    </span>
                    <span v-if="r.levelCode" class="lvl-grade-pill">{{ r.levelCode }}</span>
                    <v-tooltip v-if="r.delta" bottom content-class="fw-tip">
                      <template v-slot:activator="{ on, attrs }">
                        <span class="lvl-mapal-drill-d" v-bind="attrs" v-on="on">{{ r.delta > 0 ? '+' : '' }}{{ r.delta }}
                          {{ levelLabelWord.toLowerCase() }}{{ Math.abs(r.delta) === 1 ? '' : 's' }}</span>
                      </template>
                      <span>Evaluated {{ Math.abs(r.delta) }} {{ levelLabelWord.toLowerCase() }}{{ Math.abs(r.delta) === 1 ? '' : 's' }}
                        {{ r.delta > 0 ? 'above' : 'below' }} where this grade links company-wide. Cases like this are
                        what make a grade divergent.</span>
                    </v-tooltip>
                  </div>
                  <button v-if="g.fold" type="button" class="lvl-mapal-drill-more" @click="drillAll = true">
                    Show all {{ g.items.length }}
                  </button>
                </div>
              </section>
            </template>
            <div v-else class="lvl-mapal-drill-f">No role on this register carries this grade any more.</div>
          </div>
        </div>
      </div>

      <!-- ============================= splits ============================= -->
      <div v-if="showFg.length && splits.length" class="lvl-mapal-splits">
        <div class="lvl-mapal-splits-h">
          <v-icon size="15">mdi-call-split</v-icon>
          Where the same grade sits at different {{ levelLabelWord.toLowerCase() }}s
        </div>
        <div v-for="s in splits.slice(0, 6)" :key="s.key + s.familyGroup" class="lvl-mapal-split">
          <span class="lvl-grade-pill lvl-mapal-g is-div">{{ s.grade }}</span>
          <span class="lvl-mapal-split-t">
            company-wide <b>{{ backboneNames(s.home) }}</b>, but <b>{{ s.familyGroup }}</b> evidence puts
            it at <b>{{ backboneNames(s.lf_level_id) }}</b> &mdash; so this grade is split by function.
          </span>
        </div>
        <div class="lvl-mapal-splits-f">
          Splits are scoped at job family group level, and only appear where the evidence actually
          disagrees. Publishing a function-specific link is a later slice; today a split routes to review.
        </div>
      </div>
    </template>

    <GradeMappingPanel
      v-if="panelOpen"
      :levelling-framework="levellingFramework"
      :schemes="schemes"
      :suggestions="suggestions"
      :map="map"
      :versions="versions"
      :published="published"
      :can-govern="canGovern"
      :roles="roles"
      @close="panelOpen = false"
      @saved="onPanelSaved"
    />
  </div>
</template>

<style lang="scss" scoped>
/* Ported from the prototype's lvl.css .lvl-mapal-* / .lvl-fwal-* / .lvl-grade-pill
   block, with --v3-* mapped onto the app-wide --cc-* ramp. The prototype's
   --v3-revise (hand-set) has no --cc- twin; violet is the nearest neutral marker
   that is not already carrying a meaning on these surfaces. */

.lvl-fwal {
  display: flex;
  flex-direction: column;
  gap: 14px;
  /* The tab frame (.framework-content > .v-card__text) is a fixed-height flex column
     with overflow:hidden, and every other tab puts its own scroller inside. This view
     is one content column rather than a table, so the view itself is that scroller —
     without this it is simply clipped at the fold with no way to reach the bottom.
     flex/min-height rather than the max-height: calc(100vh - Npx) used elsewhere,
     which is wrong inside the page shell, where the frame is height:100% of a wrap
     that is already offset by the page head. */
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  /* so the last card is not glued to the bottom edge of the scroll area */
  padding-bottom: 16px;

  /* A column flex container hands its children a default flex-shrink of 1, so with a
     definite height they COMPRESS to fit instead of overflowing — overflow-y above
     would then never have anything to scroll, and tall content would simply be
     squashed. Pinning the sections to their natural height is what turns the overflow
     into an actual scroll. */
  > * {
    flex-shrink: 0;
  }

  font-family: var(--cc-font, 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif);
}

.lvl-fwal-loading,
.lvl-fwal-msg {
  padding: 22px 4px;
  font-size: 13px;
  color: var(--cc-text-2, #5b6472);
  line-height: 1.55;
}
.lvl-fwal-msg.is-error { color: var(--cc-danger, #d64545); }
.lvl-fwal-msg.is-warn {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  padding: 12px 14px;
  border: 1px solid var(--cc-warn-bd, #eec391);
  background: var(--cc-warn-bg, #fcf5ee);
  color: var(--cc-warn-fg, #d97706);
  border-radius: 10px;
  font-size: 12.5px;
}

/* ---------------------------------------------------------------------- intro */

.lvl-fwal-intro {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 14px 16px;
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: 12px;
  background: var(--cc-surface, #ffffff);
}
.lvl-fwal-intro-ic {
  flex: none;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  display: grid;
  place-items: center;
  background: color-mix(in srgb, var(--cc-accent, #4f46e5) 10%, transparent);
  color: var(--cc-accent, #4f46e5);

  ::v-deep .v-icon { color: inherit; }
}
.lvl-fwal-intro-main {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;

  .t { font-size: 14px; font-weight: 700; color: var(--cc-text, #1f242d); }
  .s { font-size: 12.5px; color: var(--cc-text-2, #5b6472); line-height: 1.55; text-wrap: pretty; }
}
.lvl-fwal-how {
  display: block;
  margin-top: 7px;
  padding-top: 7px;
  border-top: 1px solid var(--cc-border-soft, #eef0f3);
}
.lvl-fwal-more {
  border: 0;
  background: transparent;
  padding: 0;
  font: inherit;
  font-weight: 650;
  color: var(--cc-accent, #4f46e5);
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}
.lvl-fwal-actions {
  flex: none;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}
.lvl-fwal-status {
  font-size: 11.5px;
  font-weight: 650;
  color: var(--cc-text-3, #97a0af);
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: 999px;
  padding: 4px 10px;
  white-space: nowrap;
  cursor: help;

  &.is-pub {
    color: var(--cc-accent, #4f46e5);
    border-color: color-mix(in srgb, var(--cc-accent, #4f46e5) 40%, transparent);
    background: color-mix(in srgb, var(--cc-accent, #4f46e5) 7%, transparent);
  }
}
.lvl-fwal-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 1px solid color-mix(in srgb, var(--cc-accent, #4f46e5) 45%, transparent);
  background: var(--cc-surface, #ffffff);
  border-radius: 9px;
  padding: 6px 12px;
  font: inherit;
  font-size: 12.5px;
  font-weight: 650;
  color: var(--cc-accent, #4f46e5);
  cursor: pointer;
  white-space: nowrap;

  &:hover { background: color-mix(in srgb, var(--cc-accent, #4f46e5) 8%, transparent); }
  ::v-deep .v-icon { color: inherit; }
}

/* ----------------------------------------------------------------------- chips */

.lvl-fwal-fgbar { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; padding: 2px 0; }
.lvl-fwal-fglabel {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 650;
  color: var(--cc-text-2, #5b6472);

  &--sep { margin-left: 10px; padding-left: 14px; border-left: 1px solid var(--cc-border, #e5e7eb); }
  ::v-deep .v-icon { color: inherit; }
}
.lvl-chipbtn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1px solid var(--cc-border, #e5e7eb);
  background: var(--cc-surface, #ffffff);
  border-radius: 999px;
  padding: 4px 11px;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  color: var(--cc-text-2, #5b6472);
  cursor: pointer;

  &.is-on {
    color: var(--cc-accent, #4f46e5);
    border-color: color-mix(in srgb, var(--cc-accent, #4f46e5) 45%, transparent);
    background: color-mix(in srgb, var(--cc-accent, #4f46e5) 8%, transparent);
  }
  ::v-deep .v-icon { color: inherit; }
}

/* ------------------------------------------------------------------ the grid */

/* Vuetify renders a tooltip's activator as a SIBLING of an empty
   <span class="v-tooltip">, not inside it. Every grid and flex container in this view
   would otherwise place that empty span as an item of its own — shifting every grade
   one column along in .lvl-mapal-grid. Taking them out of layout everywhere is both
   simpler and safer than listing each container. */
.v-tooltip { display: contents; }

/* Every grid item that is a tooltip sits in one of these. Vuetify lifts the activator
   out of <span class="v-tooltip"> behind Vue's back, so when toggling a column off
   made Vue MOVE a row's level tooltip to close the gap, only the empty span moved and
   the label stayed put — every row slid one column along. Vue moves the wrapper, and
   the lifted activator is inside it. Out of layout, so its content is the grid item. */
.lvl-mapal-slot { display: contents; }

.lvl-mapal-key { display: flex; flex-wrap: wrap; gap: 14px; align-items: center; }
.lvl-mapal-key-i {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11.5px;
  color: var(--cc-text-3, #97a0af);

  .sw.is-bb { width: 14px; height: 14px; border-radius: 4px; background: var(--cc-accent, #4f46e5); }
}

.lvl-mapal-grid { display: grid; gap: 8px; align-items: stretch; }
.lvl-mapal-h {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .04em;
  color: var(--cc-text-3, #97a0af);
  align-self: end;
  padding: 0 2px 4px;
  cursor: help;

  &--other { color: var(--cc-text-2, #5b6472); }
}
.lvl-mapal-bb {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 1px;
  background: color-mix(in srgb, var(--cc-accent, #4f46e5) 8%, var(--cc-surface, #fff));
  border: 1px solid color-mix(in srgb, var(--cc-accent, #4f46e5) 20%, transparent);
  border-radius: 9px;
  padding: 9px 12px;
  cursor: help;

  b { font-size: 12.5px; font-weight: 700; line-height: 1.3; color: color-mix(in srgb, var(--cc-accent, #4f46e5) 82%, var(--cc-text, #1f242d)); }
  i { font-style: normal; font-size: 10.5px; color: var(--cc-text-2, #5b6472); }
}
.lvl-mapal-cell {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  align-items: center;
  align-content: center;
  border: 1px solid var(--cc-border-soft, #eef0f3);
  border-radius: 9px;
  padding: 7px 9px;
  min-height: 44px;
  background: var(--cc-surface, #ffffff);

  &--fg { background: color-mix(in srgb, var(--cc-warn-fg, #d97706) 3%, var(--cc-surface, #fff)); }
  &--other { background: var(--cc-canvas, #fafbfc); border-style: dashed; }
}
.lvl-mapal-none { color: var(--cc-text-3, #97a0af); opacity: .5; font-size: 12px; }

.lvl-grade-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 6px;
  border: 1px solid var(--cc-border, #e5e7eb);
  background: var(--cc-canvas, #fafbfc);
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: .02em;
  color: var(--cc-text-2, #5b6472);
  white-space: nowrap;
  max-width: 100%;
}
.lvl-mapal-g {
  cursor: help;

  &.is-btn { cursor: pointer; font-family: inherit; }
  &.is-btn:hover { border-color: color-mix(in srgb, var(--cc-accent, #4f46e5) 55%, transparent); }
  &.is-btn.is-open { outline: 2px solid var(--cc-accent, #4f46e5); outline-offset: 1px; }
  &.is-conf { border-color: color-mix(in srgb, var(--cc-ok-fg, #0e9f5b) 50%, transparent); background: var(--cc-surface, #fff); }
  &.is-thin { border-style: dashed; background: var(--cc-surface, #fff); color: var(--cc-text-3, #97a0af); }
  &.is-div {
    color: var(--cc-text, #1f242d);
    border-color: color-mix(in srgb, var(--cc-warn-fg, #d97706) 55%, transparent);
    background: color-mix(in srgb, var(--cc-warn-fg, #d97706) 18%, var(--cc-surface, #fff));
  }
  /* A hand-set link keeps the confidence of its EVIDENCE and is marked separately —
     a moved grade still reading "confident" with no sign anything changed is the bug
     this dot exists to prevent. */
  &.is-ov {
    position: relative;

    &::after {
      content: "";
      position: absolute;
      top: -2px;
      right: -2px;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: var(--cc-violet-fg, #6d5ae6);
      box-shadow: 0 0 0 1.5px var(--cc-surface, #fff);
    }
  }
  ::v-deep .v-icon { color: inherit; }
}

/* ---------------------------------------------------------------------- drill */

/* design: .lvl-drill - a right-hand panel over a scrim. */
.lvl-drill {
  position: fixed;
  inset: 0;
  z-index: 1600;
  display: flex;
  justify-content: flex-end;
}
.lvl-drill-scrim {
  position: absolute;
  inset: 0;
  background: rgba(16, 24, 40, 0.34);
}
.lvl-drill-panel {
  position: relative;
  width: min(560px, 100%);
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--cc-surface, #ffffff);
  box-shadow: var(--cc-shadow-lg, 0 12px 32px rgba(16, 24, 40, 0.14));
  animation: lvl-drill-slide 200ms cubic-bezier(.4, 0, .2, 1);
}
@keyframes lvl-drill-slide {
  from { transform: translateX(20px); opacity: .7; }
  to { transform: none; opacity: 1; }
}
@media (prefers-reduced-motion: reduce) { .lvl-drill-panel { animation: none; } }
.lvl-drill-head {
  flex: 0 0 auto;
  padding: 20px 22px;
  border-bottom: 1px solid var(--cc-border, #e5e7eb);
}
.lvl-drill-head-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.lvl-drill-body {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 18px 22px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.lvl-mapal-drill-hd {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;

  h3 { margin: 0; font-size: 17px; font-weight: 700; letter-spacing: -.01em; color: var(--cc-text, #1f242d); }
}
.lvl-mapal-drill-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;

  .lvl-mapal-drill-n { margin-left: 0; font-size: 12.5px; }
}
.lvl-mapal-drill-grp + .lvl-mapal-drill-grp { margin-top: 18px; }
.lvl-mapal-drill-gh { display: flex; align-items: baseline; gap: 8px; padding: 0 2px 8px; }
.lvl-mapal-drill {
  border: 1px solid var(--cc-border, #e5e7eb);
  border-radius: 11px;
  overflow: hidden;
  background: var(--cc-surface, #ffffff);
}
.lvl-mapal-drill-t { font-size: 12.5px; font-weight: 650; color: var(--cc-text, #1f242d); }
.lvl-mapal-drill-n { margin-left: auto; font-size: 11.5px; color: var(--cc-text-2, #5b6472); }
.lvl-mapal-drill-x {
  flex: none;
  border: 0;
  background: transparent;
  cursor: pointer;
  color: var(--cc-text-3, #97a0af);
  padding: 0;

  ::v-deep .v-icon { color: inherit; }
}
.lvl-mapal-drill-who {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.lvl-mapal-drill-more {
  display: block;
  width: 100%;
  padding: 8px 14px;
  border: 0;
  background: none;
  text-align: left;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  color: var(--cc-accent, #4f46e5);
  cursor: pointer;

  &:hover { background: var(--cc-canvas, #fafbfc); }
}
.lvl-mapal-drill-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  border-bottom: 1px solid var(--cc-border-soft, #eef0f3);
  font-size: 12.5px;

  &:last-child { border-bottom: 0; }
  &.is-off { background: color-mix(in srgb, var(--cc-warn-fg, #d97706) 5%, transparent); }
  .t { font-weight: 650; color: var(--cc-text, #1f242d); }
  .f { color: var(--cc-text-2, #5b6472); }
  .lvl-grade-pill { margin-left: auto; }
  .lvl-mapal-drill-d { margin-left: 0; }
}
.lvl-mapal-drill-d {
  display: inline-flex;
  align-items: center;
  padding: 2px 7px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  color: var(--cc-text, #1f242d);
  border: 1px solid color-mix(in srgb, var(--cc-warn-fg, #d97706) 55%, transparent);
  background: color-mix(in srgb, var(--cc-warn-fg, #d97706) 18%, var(--cc-surface, #fff));
  cursor: help;
}
.lvl-mapal-drill-f { padding: 9px 14px; font-size: 11.5px; color: var(--cc-text-2, #5b6472); line-height: 1.5; }

.lvl-map-state {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid var(--cc-border, #e5e7eb);
  color: var(--cc-text-3, #97a0af);

  &.is-ov {
    color: var(--cc-violet-fg, #6d5ae6);
    border-color: color-mix(in srgb, var(--cc-violet-fg, #6d5ae6) 45%, transparent);
    background: var(--cc-violet-bg, #efecfc);
    cursor: help;
  }
}

/* --------------------------------------------------------------------- splits */

.lvl-mapal-splits {
  border: 1px solid color-mix(in srgb, var(--cc-warn-fg, #d97706) 35%, transparent);
  border-radius: 11px;
  overflow: hidden;
  background: var(--cc-surface, #ffffff);
}
.lvl-mapal-splits-h {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 10px 14px;
  font-size: 12.5px;
  font-weight: 700;
  color: var(--cc-text, #1f242d);
  background: color-mix(in srgb, var(--cc-warn-fg, #d97706) 8%, transparent);
  border-bottom: 1px solid color-mix(in srgb, var(--cc-warn-fg, #d97706) 22%, transparent);
}
.lvl-mapal-split {
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 9px 14px;
  border-bottom: 1px solid var(--cc-border-soft, #eef0f3);
}
.lvl-mapal-split-t { font-size: 12.5px; color: var(--cc-text-2, #5b6472); line-height: 1.5; }
.lvl-mapal-splits-f { padding: 9px 14px; font-size: 11.5px; color: var(--cc-text-3, #97a0af); line-height: 1.5; }

@media (max-width: 900px) {
  .lvl-fwal-intro { flex-wrap: wrap; }
  .lvl-fwal-actions { flex-direction: row; margin-left: auto; }
}
</style>
